import { useState, useEffect, useCallback } from 'react';
import {
  Receipt, Loader2, AlertTriangle, Sparkles, RefreshCw, CheckCircle2, XCircle,
  TrendingDown, Calendar, Building2, ArrowRight, Info, Shield, Clock,
} from 'lucide-react';
import {
  getHarvestOpportunities, planHarvest, executeHarvest, getHarvestHistory,
  getExchangeConnections,
} from '../services/api';
import { InfoTip } from '../components/ui';

function formatUsd(n) {
  if (n == null || !Number.isFinite(Number(n))) return '—';
  const v = Number(n);
  if (Math.abs(v) >= 1000) return `$${v.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  return `$${v.toFixed(2)}`;
}
function formatNum(n, decimals = 6) {
  if (n == null || !Number.isFinite(Number(n))) return '—';
  return Number(n).toLocaleString(undefined, { maximumFractionDigits: decimals });
}

export default function TaxHarvestPage() {
  const [opportunities, setOpportunities] = useState(null);
  const [totals, setTotals] = useState({ count: 0, totalLossUsd: 0, totalEstSavingsUsd: 0 });
  const [exchanges, setExchanges] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState('opportunities'); // opportunities | history
  const [minHarvestUsd, setMinHarvestUsd] = useState(25);
  const [taxRatePct, setTaxRatePct] = useState(30);
  const [planModal, setPlanModal] = useState(null); // { opportunity, replacement, exchangeId, plan, washSaleConflict, executing }

  const load = useCallback(async () => {
    try {
      setLoading(true); setError(null);
      const [oppRes, exRes, histRes] = await Promise.all([
        getHarvestOpportunities({ minHarvestUsd, taxRatePct }),
        getExchangeConnections().catch(() => ({ data: { exchanges: [] } })),
        getHarvestHistory({ limit: 50 }).catch(() => ({ data: { entries: [] } })),
      ]);
      const oppData = oppRes?.data || oppRes;
      setOpportunities(oppData?.opportunities || []);
      setTotals(oppData?.totals || { count: 0, totalLossUsd: 0, totalEstSavingsUsd: 0 });
      // exchange response shape: { exchanges: [{ exchangeId, ... }] } from /exchange/connections
      const exData = exRes?.data || exRes;
      setExchanges(exData?.exchanges || []);
      setHistory((histRes?.data || histRes)?.entries || []);
    } catch (e) {
      setError(e?.message || 'Failed to load tax harvest data');
    } finally {
      setLoading(false);
    }
  }, [minHarvestUsd, taxRatePct]);

  useEffect(() => { load(); }, [load]);

  function openPlanModal(opportunity, replacement) {
    setPlanModal({
      opportunity,
      replacement,
      exchangeId: exchanges[0]?.exchangeId || '',
      plan: null,
      washSaleConflict: null,
      executing: false,
      result: null,
      error: null,
    });
  }

  async function previewPlan(state) {
    try {
      setPlanModal(s => ({ ...s, error: null, plan: null, washSaleConflict: null, loading: true }));
      const res = await planHarvest({
        lotId: state.opportunity.lotId,
        replacementSymbol: state.replacement.symbol,
        exchangeId: state.exchangeId,
      });
      const data = res?.data || res;
      setPlanModal(s => ({
        ...s,
        plan: data?.plan,
        washSaleConflict: data?.washSaleConflict,
        preflight: data?.preflight,
        loading: false,
      }));
    } catch (e) {
      setPlanModal(s => ({ ...s, error: e?.message || 'Failed to preview plan', loading: false }));
    }
  }

  async function runExecute(state, acknowledgeWashSale = false) {
    try {
      setPlanModal(s => ({ ...s, executing: true, error: null, result: null }));
      const res = await executeHarvest({
        lotId: state.opportunity.lotId,
        replacementSymbol: state.replacement.symbol,
        exchangeId: state.exchangeId,
        acknowledgeWashSale,
      });
      const data = res?.data || res;
      setPlanModal(s => ({ ...s, executing: false, result: data }));
      // refresh opportunities + history after a beat
      setTimeout(load, 1500);
    } catch (e) {
      setPlanModal(s => ({ ...s, executing: false, error: e?.message || 'Execute failed' }));
    }
  }

  return (
    <div className="max-w-7xl mx-auto space-y-6 animate-fade-in">
      {/* Hero */}
      <div className="relative overflow-hidden bg-gradient-to-br from-amber-500/10 via-dark-800/80 to-dark-900 rounded-2xl border border-amber-500/20 p-6">
        <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-amber-400/10 blur-3xl pointer-events-none" />
        <div className="relative flex flex-wrap items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            <div className="w-12 h-12 rounded-xl bg-amber-500/20 border border-amber-400/30 flex items-center justify-center flex-shrink-0">
              <Receipt className="w-6 h-6 text-amber-400" />
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-2xl font-bold text-white">Tax-Loss Auto-Harvest</h1>
                <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-400 border border-amber-500/30 font-mono">Elite · Trader's Suite</span>
              </div>
              <p className="text-xs text-amber-200/70 mt-1 flex items-center gap-1.5">
                <Sparkles className="w-3 h-3" /> Booked losses you can claim now, with wash-sale-safe replacement assets so you stay in the market.
                <InfoTip side="bottom">Wash-sale rule = 30 days BEFORE AND AFTER. Sell ETH at a loss + rebuy ETH within 30 days = disallowed. Replacement assets (WETH, stETH) are correlated but legally distinct. NOT tax advice.</InfoTip>
              </p>
            </div>
          </div>
          <button
            onClick={load}
            disabled={loading}
            className="text-xs px-3 py-2 rounded-lg bg-amber-500/20 border border-amber-500/40 text-amber-200 hover:bg-amber-500/30 transition flex items-center gap-1.5 disabled:opacity-50"
          >
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            Rescan
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 text-sm text-red-300 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 flex-shrink-0" /> {error}
        </div>
      )}

      {/* KPI cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <KpiCard
          icon={<TrendingDown className="w-4 h-4 text-red-300" />}
          label="Harvestable lots"
          value={totals.count}
          sub={totals.count === 0 ? 'No opportunities right now' : `${totals.count} lot${totals.count === 1 ? '' : 's'} below your threshold`}
        />
        <KpiCard
          icon={<Receipt className="w-4 h-4 text-amber-300" />}
          label="Total unrealized loss"
          value={formatUsd(totals.totalLossUsd)}
          sub="Could be booked as realized loss"
        />
        <KpiCard
          icon={<Sparkles className="w-4 h-4 text-emerald-300" />}
          label={`Est tax savings @ ${taxRatePct}%`}
          value={formatUsd(totals.totalEstSavingsUsd)}
          sub="Estimate only. Not tax advice."
          tone="positive"
        />
      </div>

      {/* Filters */}
      <div className="bg-dark-800/60 border border-dark-700 rounded-2xl p-4 grid grid-cols-1 sm:grid-cols-2 gap-3 items-end">
        <div>
          <label className="text-[11px] uppercase tracking-wider text-gray-500 mb-1 flex items-center gap-1">
            Min harvest loss (USD)
            <InfoTip side="top">Below $25-50 fees + slippage eat too much of the savings. $50 is the practical floor for most users; Pro-tier traders with low-fee exchange tiers can drop to $25.</InfoTip>
          </label>
          <input
            type="number" min="1" value={minHarvestUsd}
            onChange={e => setMinHarvestUsd(Number(e.target.value) || 1)}
            className="w-full px-3 py-2 rounded-lg bg-dark-900/60 border border-dark-700 text-white text-sm focus:border-amber-500 focus:outline-none"
          />
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-wider text-gray-500 mb-1 flex items-center gap-1">
            Your marginal tax rate (%) — combined fed + state
            <InfoTip side="top">Typical: 22-37% federal + your state. Used ONLY to estimate savings (rate × loss). Real tax savings depend on your final tax-year situation — talk to a CPA.</InfoTip>
          </label>
          <input
            type="number" min="0" max="100" value={taxRatePct}
            onChange={e => setTaxRatePct(Number(e.target.value) || 0)}
            className="w-full px-3 py-2 rounded-lg bg-dark-900/60 border border-dark-700 text-white text-sm focus:border-amber-500 focus:outline-none"
          />
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-2 border-b border-dark-700">
        <TabButton active={tab === 'opportunities'} onClick={() => setTab('opportunities')} label={`Opportunities (${opportunities?.length || 0})`} />
        <TabButton active={tab === 'history'} onClick={() => setTab('history')} label={`History (${history.length})`} />
      </div>

      {tab === 'opportunities' && (
        loading && !opportunities
          ? <div className="text-center py-10 text-gray-400"><Loader2 className="w-6 h-6 animate-spin mx-auto mb-2 text-amber-400" />Scanning lots...</div>
          : <OpportunitiesList opportunities={opportunities || []} exchanges={exchanges} onHarvest={openPlanModal} />
      )}

      {tab === 'history' && <HistoryList history={history} />}

      {planModal && (
        <PlanModal
          state={planModal}
          setState={setPlanModal}
          exchanges={exchanges}
          onPreview={previewPlan}
          onExecute={runExecute}
        />
      )}
    </div>
  );
}

function KpiCard({ icon, label, value, sub, tone = 'neutral' }) {
  const cls = tone === 'positive' ? 'text-emerald-300' : tone === 'negative' ? 'text-red-300' : 'text-white';
  return (
    <div className="bg-dark-800/60 border border-dark-700 rounded-xl p-4">
      <div className="text-[10px] uppercase tracking-wider text-gray-500 font-mono flex items-center gap-1.5 mb-2">
        {icon}<span>{label}</span>
      </div>
      <div className={`text-2xl font-bold ${cls}`}>{value}</div>
      <div className="text-[11px] text-gray-500 mt-1">{sub}</div>
    </div>
  );
}

function TabButton({ active, onClick, label }) {
  return (
    <button
      onClick={onClick}
      className={`text-xs px-3 py-2 -mb-px border-b-2 transition ${active ? 'border-amber-500 text-amber-300' : 'border-transparent text-gray-500 hover:text-gray-300'}`}
    >{label}</button>
  );
}

function OpportunitiesList({ opportunities, exchanges, onHarvest }) {
  if (opportunities.length === 0) {
    return (
      <div className="text-center py-10 bg-dark-800/50 border border-dark-700 rounded-2xl">
        <CheckCircle2 className="w-10 h-10 text-emerald-400 mx-auto mb-3" />
        <h3 className="text-lg font-semibold text-white mb-1">No harvest opportunities right now</h3>
        <p className="text-sm text-gray-400 max-w-md mx-auto">
          All your priced lots are either above the threshold or already in profit. Lower the min-loss filter or check back when the market dips.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {exchanges.length === 0 && (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-3 text-xs text-amber-200 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 flex-shrink-0" />
          No connected exchanges. Connect one in <span className="text-amber-100 underline">Settings → Exchanges</span> to execute harvests.
        </div>
      )}
      {opportunities.map(o => (
        <OpportunityCard key={o.lotId} opportunity={o} canHarvest={exchanges.length > 0} onHarvest={onHarvest} />
      ))}
    </div>
  );
}

function OpportunityCard({ opportunity: o, canHarvest, onHarvest }) {
  return (
    <div className="bg-dark-800/60 border border-dark-700 rounded-2xl p-5">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
        <div>
          <div className="flex items-center gap-2">
            <span className="text-base font-bold text-white font-mono">{o.base}</span>
            <span className={`text-[10px] uppercase font-mono px-1.5 py-0.5 rounded border ${o.lotTerm === 'long-term' ? 'bg-blue-500/15 text-blue-300 border-blue-500/30' : 'bg-amber-500/15 text-amber-300 border-amber-500/30'}`}>{o.lotTerm}</span>
            <span className="text-[11px] text-gray-500 flex items-center gap-1"><Clock className="w-3 h-3" />{o.holdingDays}d held</span>
          </div>
          <div className="text-[11px] text-gray-500 mt-0.5">
            Opened {o.openedAt ? new Date(o.openedAt).toLocaleDateString() : '—'} · {formatNum(o.baseRemaining)} {o.base}
          </div>
        </div>
        <div className="text-right">
          <div className="text-lg font-bold text-red-300">−{formatUsd(o.unrealizedLossUsd)}</div>
          <div className="text-[11px] text-gray-500">unrealized loss</div>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs mb-4">
        <Cell label="Cost basis" value={formatUsd(o.costBasisUsd)} />
        <Cell label="Current value" value={formatUsd(o.currentValueUsd)} />
        <Cell label="Current price" value={formatUsd(o.currentPriceUsd)} />
        <Cell label="Est tax savings" value={formatUsd(o.estTaxSavingsUsd)} tone="emerald" />
      </div>

      <div className="border-t border-dark-700/60 pt-3">
        <div className="text-[11px] uppercase tracking-wider text-gray-500 font-mono mb-2">Replace with (stay in the market)</div>
        {o.replacementOptions.length === 0 ? (
          <div className="text-xs text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-lg p-2">
            No curated replacement for {o.base}. Harvest manually if you don't mind being market-out.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            {o.replacementOptions.map(r => (
              <button
                key={r.symbol}
                onClick={() => canHarvest && onHarvest(o, r)}
                disabled={!canHarvest}
                className="text-left rounded-lg border border-dark-600 bg-dark-900/40 hover:border-amber-500/40 hover:bg-amber-500/5 transition p-3 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <div className="flex items-center justify-between mb-1">
                  <div className="text-sm font-semibold text-white font-mono">{r.symbol}</div>
                  <ArrowRight className="w-3.5 h-3.5 text-amber-400" />
                </div>
                <div className="text-[11px] text-gray-400">{r.name}</div>
                <div className="text-[10px] text-gray-500 mt-1">{r.rationale}</div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Cell({ label, value, tone = 'neutral' }) {
  const cls = tone === 'emerald' ? 'text-emerald-300' : 'text-white';
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-gray-500 font-mono mb-0.5">{label}</div>
      <div className={`font-semibold ${cls}`}>{value}</div>
    </div>
  );
}

function PlanModal({ state, setState, exchanges, onPreview, onExecute }) {
  const o = state.opportunity;
  const r = state.replacement;
  const close = () => setState(null);

  // If user has changed exchange or first opens modal, fetch plan
  useEffect(() => {
    if (state.exchangeId && !state.plan && !state.loading && !state.result && !state.error) {
      onPreview(state);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.exchangeId]);

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={close}>
      <div onClick={e => e.stopPropagation()} className="bg-dark-900 border border-dark-700 rounded-2xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-bold text-white">Harvest plan</h2>
              <span className="text-[10px] uppercase font-mono px-2 py-0.5 rounded border bg-amber-500/15 text-amber-300 border-amber-500/30">Preview</span>
            </div>
            <div className="text-xs text-gray-400 mt-0.5">
              Sell {formatNum(o.baseRemaining)} <span className="font-mono text-white">{o.base}</span> · Buy <span className="font-mono text-white">{r.symbol}</span>
            </div>
          </div>
          <button onClick={close} className="text-gray-400 hover:text-white"><XCircle className="w-5 h-5" /></button>
        </div>

        <div className="mb-4">
          <label className="text-[11px] uppercase tracking-wider text-gray-500 mb-1 block flex items-center gap-1.5"><Building2 className="w-3 h-3" />Execute on exchange</label>
          <select
            value={state.exchangeId}
            onChange={e => setState(s => ({ ...s, exchangeId: e.target.value, plan: null, washSaleConflict: null, result: null, error: null }))}
            className="w-full px-3 py-2 rounded-lg bg-dark-900/60 border border-dark-700 text-white text-sm capitalize"
          >
            {exchanges.length === 0 && <option value="">No exchanges connected</option>}
            {exchanges.map(ex => (
              <option key={ex.exchangeId} value={ex.exchangeId}>{ex.exchangeId}</option>
            ))}
          </select>
        </div>

        {state.loading && (
          <div className="text-center py-6 text-gray-400 text-sm"><Loader2 className="w-5 h-5 animate-spin mx-auto mb-2 text-amber-400" />Building plan...</div>
        )}

        {state.error && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-xs text-red-300 mb-3 flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />{state.error}
          </div>
        )}

        {state.plan && !state.result && (
          <>
            <div className="bg-dark-800/60 border border-dark-700 rounded-xl p-4 mb-3 space-y-3">
              <Row label="Sell" value={`${formatNum(state.plan.sell.amount)} ${o.base} (market)`} />
              <Row label="Expected proceeds" value={formatUsd(state.plan.expectedSellProceedsUsd)} />
              <Row label="Then buy" value={`${formatUsd(state.plan.buyBudgetUsd)} of ${r.symbol} (market)`} />
              <Row label="Fee buffer kept in stable" value={formatUsd(state.plan.feeBufferUsd)} />
              <div className="border-t border-dark-700/60 pt-2">
                <Row label="Realized loss booked" value={`−${formatUsd(state.plan.estimatedRealizedLossUsd)}`} tone="negative" />
                <Row label="Estimated tax savings" value={`+${formatUsd(state.plan.estimatedTaxSavingsUsd)}`} tone="positive" />
              </div>
            </div>

            {state.preflight && !state.preflight.ok && (
              <div className="bg-red-500/10 border border-red-500/40 rounded-xl p-3 text-xs text-red-200 mb-3">
                <div className="flex items-center gap-1.5 font-semibold mb-1">
                  <XCircle className="w-3.5 h-3.5" /> Pre-flight check failed: {state.preflight.reason}
                </div>
                <div className="text-red-200/90">{state.preflight.detail}</div>
              </div>
            )}

            {state.washSaleConflict && (
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-3 text-xs text-amber-200 mb-3">
                <div className="flex items-center gap-1.5 font-semibold mb-1">
                  <Shield className="w-3.5 h-3.5" /> Wash-sale risk detected
                </div>
                <div className="text-amber-200/90">{state.washSaleConflict.advice}</div>
                <div className="text-[11px] text-amber-300/70 mt-1 flex items-center gap-1">
                  <Calendar className="w-3 h-3" /> Most recent buy: {new Date(state.washSaleConflict.mostRecent.ts).toLocaleString()}
                </div>
                {state.washSaleConflict.bySourceCount && (
                  <div className="text-[11px] text-amber-300/70 mt-1">
                    Sources: {Object.entries(state.washSaleConflict.bySourceCount).map(([s, n]) => `${n}× ${s}`).join(' · ')}
                  </div>
                )}
              </div>
            )}

            <div className="bg-dark-800/40 border border-dark-700/60 rounded-lg p-3 text-[11px] text-gray-400 mb-4 flex items-start gap-2">
              <Info className="w-3.5 h-3.5 flex-shrink-0 mt-0.5 text-gray-500" />
              <div>
                Estimates are based on current prices. Market orders may slip ±0.5% during the sell+buy window (typically &lt; 5s).
                Not tax advice — consult a CPA. <strong>haythix executes the SELL first, then the BUY</strong> to keep the loss booked cleanly.
              </div>
            </div>

            <button
              onClick={() => onExecute(state, !!state.washSaleConflict)}
              disabled={state.executing || !state.exchangeId || (state.preflight && !state.preflight.ok)}
              className="w-full px-4 py-3 rounded-lg bg-amber-500/20 border border-amber-500/40 text-amber-200 hover:bg-amber-500/30 text-sm font-semibold transition flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {state.executing
                ? <><Loader2 className="w-4 h-4 animate-spin" />Executing...</>
                : (state.preflight && !state.preflight.ok)
                  ? <><XCircle className="w-4 h-4" />Cannot harvest — fix pre-flight issue first</>
                  : <><CheckCircle2 className="w-4 h-4" />{state.washSaleConflict ? 'Acknowledge wash-sale risk and harvest' : 'Harvest now'}</>}
            </button>
          </>
        )}

        {state.result && <ExecutionResult result={state.result} onClose={close} />}
      </div>
    </div>
  );
}

function Row({ label, value, tone = 'neutral' }) {
  const cls = tone === 'positive' ? 'text-emerald-300' : tone === 'negative' ? 'text-red-300' : 'text-white';
  return (
    <div className="flex items-center justify-between text-xs">
      <span className="text-gray-400">{label}</span>
      <span className={`font-semibold ${cls}`}>{value}</span>
    </div>
  );
}

function ExecutionResult({ result, onClose }) {
  const sellOk = result.sell?.status === 'filled';
  const buyOk  = result.buy?.status === 'filled';
  const overallOk = result.ok;

  return (
    <div className="space-y-3">
      <div className={`rounded-xl p-4 border ${overallOk ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-amber-500/10 border-amber-500/30'}`}>
        <div className="flex items-center gap-2 mb-2">
          {overallOk
            ? <><CheckCircle2 className="w-5 h-5 text-emerald-400" /><span className="text-emerald-300 font-semibold">Harvest complete</span></>
            : <><AlertTriangle className="w-5 h-5 text-amber-400" /><span className="text-amber-300 font-semibold">Harvest partial — review</span></>}
        </div>
        <div className="text-xs text-gray-300">Harvest ID: <span className="font-mono">{result.harvestId}</span></div>
      </div>

      <div className="bg-dark-800/60 border border-dark-700 rounded-xl p-4 space-y-3">
        <div>
          <div className="text-[11px] uppercase tracking-wider text-gray-500 font-mono mb-1 flex items-center gap-1.5">
            {sellOk ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" /> : <XCircle className="w-3.5 h-3.5 text-red-400" />}
            Sell
          </div>
          {sellOk
            ? <div className="text-xs text-gray-200">Filled {formatNum(result.sell.filledQty)} @ avg {formatUsd(result.sell.avgPrice)} · gross {formatUsd(result.sell.grossProceedsUsd)}</div>
            : <div className="text-xs text-red-300">{result.sell?.error || result.sell?.reason || 'Did not fill'}</div>}
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-wider text-gray-500 font-mono mb-1 flex items-center gap-1.5">
            {buyOk ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" /> : <XCircle className="w-3.5 h-3.5 text-red-400" />}
            Replacement buy
          </div>
          {buyOk
            ? <div className="text-xs text-gray-200">Filled {formatNum(result.buy.filledQty)} @ avg {formatUsd(result.buy.avgPrice)} · spent {formatUsd(result.buy.spentUsd)}</div>
            : <div className="text-xs text-red-300">{result.buy?.error || result.buy?.reason || 'Skipped'}{result.buy?.recovery && <div className="mt-1 text-amber-200">{result.buy.recovery}</div>}</div>}
        </div>
      </div>

      <button onClick={onClose} className="w-full px-4 py-2.5 rounded-lg bg-dark-700 border border-dark-600 text-gray-200 hover:bg-dark-600 text-sm transition">Close</button>
    </div>
  );
}

function HistoryList({ history }) {
  if (history.length === 0) {
    return (
      <div className="text-center py-10 bg-dark-800/50 border border-dark-700 rounded-2xl text-sm text-gray-400">
        No harvests yet.
      </div>
    );
  }
  return (
    <div className="bg-dark-800/60 border border-dark-700 rounded-2xl overflow-hidden">
      <table className="w-full text-xs">
        <thead className="bg-dark-900/40">
          <tr className="text-[10px] uppercase tracking-wider text-gray-500">
            <th className="text-left py-2.5 px-4 font-medium">When</th>
            <th className="text-left py-2.5 px-4 font-medium">Sold</th>
            <th className="text-left py-2.5 px-4 font-medium">Replacement</th>
            <th className="text-right py-2.5 px-4 font-medium">Loss booked</th>
            <th className="text-right py-2.5 px-4 font-medium">Est savings</th>
            <th className="text-left py-2.5 px-4 font-medium">Status</th>
          </tr>
        </thead>
        <tbody>
          {history.map(h => (
            <tr key={h.harvestId} className="border-t border-dark-700/40">
              <td className="py-2.5 px-4 text-gray-400">{new Date(h.startedAt).toLocaleString()}</td>
              <td className="py-2.5 px-4 text-white font-mono">{h.plan?.sell?.symbol}</td>
              <td className="py-2.5 px-4 text-white font-mono">{h.plan?.buy?.symbol}</td>
              <td className="py-2.5 px-4 text-right text-red-300 font-mono">−{formatUsd(h.plan?.estimatedRealizedLossUsd)}</td>
              <td className="py-2.5 px-4 text-right text-emerald-300 font-mono">+{formatUsd(h.plan?.estimatedTaxSavingsUsd)}</td>
              <td className="py-2.5 px-4">
                {h.ok
                  ? <span className="text-[10px] font-mono uppercase px-2 py-0.5 rounded border bg-emerald-500/15 text-emerald-300 border-emerald-500/30">complete</span>
                  : <span className="text-[10px] font-mono uppercase px-2 py-0.5 rounded border bg-amber-500/15 text-amber-300 border-amber-500/30">partial</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
